import { createContext, useContext, useState } from 'react'

import {
  CreateSyncRunDocument,
  CreateSyncRunMutation,
  IntegrationsCatalogCapabilityTypeEnum,
  IntegrationsSyncRunFragment,
  LastSyncRunDocument,
  MarketplaceDbIntegration,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { SyncableStateAggregation } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { useAvailableIntegration } from 'modules/integrations/hooks/SyncContext/availableIntegration'
import { useSyncRunStatusChangedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncRunStatusChangedSubscription'
import { useSyncableStateUpdatedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncableStateUpdatedSubscription'
import { SyncRunStatus } from 'modules/integrations/lib/syncRun'

export interface SyncParameter {
  key: string
  value: string
}

export type ContextParameters = Record<string, string>

export type SyncParameters = SyncParameter[]

export interface Configuration {
  capabilityType: IntegrationsCatalogCapabilityTypeEnum
  syncableStatesAggregation?: SyncableStateAggregation
}

export interface SyncContextType {
  runSynchronisation: () => Promise<void>
  syncLoading: boolean
  isSyncRunning: boolean
  contextParameters: ContextParameters
  setContextParameters: (parameters: ContextParameters) => void
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  setSyncFilterParameters: (parameters: SyncParameters) => void
  syncFilterParameters: SyncParameters
  syncParameters: () => SyncParameters
  configuration: Configuration
}

const SyncContext = createContext<SyncContextType | null>(null)

const contextParametersToArray = (params: ContextParameters): SyncParameters => {
  return Object.entries(params).map(([key, value]) => ({ key, value }))
}

export const IntegrationsSyncContextProvider = ({
  children,
  configuration,
}: {
  children: React.ReactNode
  configuration: Configuration
}) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [contextParameters, setContextParameters] = useState<ContextParameters>({})
  const [syncFilterParameters, setSyncFilterParameters] = useState<SyncParameters>([])
  const marketplaceIntegration = useAvailableIntegration(configuration.capabilityType)

  const syncEnabled = !!marketplaceIntegration

  const [sync, { loading: syncLoading }] = useMutation(CreateSyncRunDocument, {
    extractorFn: (data: CreateSyncRunMutation) => {
      return data.integrationsMutations.createIntegrationsSyncRun
    },
    update(cache, { data }) {
      const syncRun = data?.integrationsMutations?.createIntegrationsSyncRun?.syncRun
      if (!syncRun || !marketplaceIntegration) return

      // Filter out __typename to match the variables in the query
      // TODO: we need to properly handle this in the future
      const parameters = syncRun.parameters.map((parameter) => ({
        key: parameter.key,
        value: parameter.value,
      }))
      // Update the LastSyncRun query cache with the new created sync run
      // instead refetching to avoid DBs replication lag issues
      cache.writeQuery({
        query: LastSyncRunDocument,
        variables: {
          marketplaceIntegrationId: marketplaceIntegration.id,
          parameters,
        },
        data: {
          integrations: {
            syncRunsConnection: {
              nodes: [syncRun],
              __typename: 'IntegrationsSyncRunConnection',
            },
            __typename: 'integrations',
          },
        },
      })
    },
  })

  const syncParameters = () => {
    return syncFilterParameters.concat(contextParametersToArray(contextParameters))
  }

  useSyncRunStatusChangedSubscription({
    marketplaceIntegration,
    syncEnabled,
    parameters: contextParametersToArray(contextParameters),
  })

  useSyncableStateUpdatedSubscription({
    marketplaceIntegration,
    syncEnabled,
  })

  const runSynchronisation = async () => {
    if (!syncEnabled || !marketplaceIntegration) return

    await sync({
      variables: {
        capabilityType: configuration.capabilityType,
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        parameters: syncParameters(),
      },
    })
  }

  const { data: syncRunData } = useQuery(LastSyncRunDocument, {
    variables: {
      marketplaceIntegrationId: marketplaceIntegration?.id || 0,
      parameters: contextParametersToArray(contextParameters),
    },
    skip: !syncEnabled,
  })

  const lastSyncRun = syncRunData?.integrations?.syncRunsConnection.nodes[0] || null

  return (
    <SyncContext.Provider
      value={{
        runSynchronisation,
        syncLoading,
        isSyncRunning: syncLoading || lastSyncRun?.status === SyncRunStatus.RUNNING,
        contextParameters,
        setContextParameters,
        marketplaceIntegration,
        syncEnabled,
        lastSyncRun,
        setSyncFilterParameters,
        syncFilterParameters,
        syncParameters,
        configuration,
      }}
    >
      {children}
    </SyncContext.Provider>
  )
}

export function useIntegrationsSyncContext() {
  const context = useContext(SyncContext)

  if (!context) {
    throw new Error('useIntegrationsSyncContext must be used within a SyncContextProvider')
  }

  return context
}
